import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Tooltip, Tag } from "antd";
import { useSelector, useDispatch } from "react-redux";

import {
  selectCurrentProject,
  selectActiveCompany,
  selectMembers,
  selectStatuses,
} from "../../../../core/data/redux/projects/selectors";
import { updateProject } from "../../../../core/data/redux/projects/thunks";
import {
  PROJECT_PRIORITY_OPTIONS,
  STATUS_CATEGORY_COLORS,
} from "../../constants/projects.constants";
import { useProjectCapability } from "../../hooks/useProjectRole";
import UserAvatar from "../../components/UserAvatar";
import { errMsg } from "../../helpers/errMsg.helper";
import { notify } from "../../../../services/toast.config";

import { humanReadableDate } from "../../../../helper/dateTimeHelper";
import type { AppDispatch } from "../../../../core/data/redux/store";

const STATUS_MAP: Record<number, { label: string; color: string; bg: string; dot: string }> = {
  1: { label: "Active", color: "#059669", bg: "#ecfdf5", dot: "#10b981" },
  0: { label: "Inactive", color: "#dc2626", bg: "#fef2f2", dot: "#ef4444" },
};

const cardStyle: React.CSSProperties = {
  borderRadius: 14,
  boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
  border: "none",
};

const DetailsSettings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const project = useSelector(selectCurrentProject);
  const activeCompany = useSelector(selectActiveCompany);
  const members = useSelector(selectMembers);
  const statuses = useSelector(selectStatuses);

  const canUpdate = useProjectCapability(project?.id, "UPDATE_PROJECT");

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    description: "",
    priority: 0 as number,
    start_date: "",
    end_date: "",
    status: 1 as number,
  });

  useEffect(() => {
    if (project) {
      setForm({
        name: project.name || "",
        description: project.description || "",
        priority: project.priority ?? 0,
        start_date: project.start_date?.slice(0, 10) || "",
        end_date: project.end_date?.slice(0, 10) || "",
        status: project.status ?? 1,
      });
    }
  }, [project]);

  const handleSave = useCallback(async () => {
    if (!project?.id) return;
    setSaving(true);
    try {
      await dispatch(
        updateProject({ projectId: project.id, payload: form })
      ).unwrap();
      notify.success("Project details updated");
      setEditing(false);
    } catch (e: any) {
      notify.error(errMsg(e, "Failed to update project"));
    } finally {
      setSaving(false);
    }
  }, [dispatch, project?.id, form]);

  const priorityLabel = useMemo(
    () => PROJECT_PRIORITY_OPTIONS.find((o) => o.value === project?.priority)?.label || "—",
    [project?.priority]
  );

  const creatorName = useMemo(() => {
    if (!project) return "—";
    const member = members.find((m: any) => Number(m.user_id) === Number(project.created_by));
    return member?.user_snapshot?.name || (project as any).creator_snapshot?.name || project.created_by || "—";
  }, [project, members]);

  const sc = STATUS_MAP[project?.status ?? 1] || STATUS_MAP[1];

  if (!project) {
    return (
      <div
        className="d-flex justify-content-center align-items-center bg-white"
        style={{ minHeight: 300, borderRadius: 14, border: "1px solid #e2e8f0" }}
      >
        <div className="text-center">
          <i className="ti ti-folder-off" style={{ fontSize: 48, color: "#cbd5e1" }} />
          <h5 className="mt-3 mb-2 fw-semibold" style={{ color: "#334155" }}>
            No Project Selected
          </h5>
          <p className="text-muted mb-0">Select a project to view its details.</p>
        </div>
      </div>
    );
  }

  const stats = project.stats || {
    total: 0,
    by_status_category: { TODO: 0, IN_PROGRESS: 0, DONE: 0 },
  };
  const total = stats.total || 0;
  const done = stats.by_status_category?.DONE || 0;
  const inProg = stats.by_status_category?.IN_PROGRESS || 0;
  const completionPct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className="d-flex flex-column gap-3">
      {/* ─── Project Info Card ─── */}
      <div className="card" style={cardStyle}>
        <div className="card-header border-0 pb-0 d-flex align-items-center justify-content-between">
          <h6 className="mb-0 d-flex align-items-center gap-2" style={{ color: "#0f172a" }}>
            <i className="ti ti-info-circle" style={{ fontSize: 18, color: "#3b82f6" }} />
            Project Details
          </h6>
          {!editing ? (
            canUpdate && (
              <button
                className="btn btn-sm d-flex align-items-center gap-1"
                style={{
                  background: "#eff6ff",
                  color: "#2563eb",
                  borderRadius: 8,
                  fontWeight: 500,
                  fontSize: 13,
                  border: "none",
                }}
                onClick={() => setEditing(true)}
              >
                <i className="ti ti-edit" style={{ fontSize: 15 }} />
                Edit
              </button>
            )
          ) : (
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm"
                style={{
                  background: "#f1f5f9",
                  color: "#475569",
                  borderRadius: 8,
                  fontWeight: 500,
                  fontSize: 13,
                  border: "none",
                }}
                onClick={() => {
                  setEditing(false);
                  setForm({
                    name: project.name || "",
                    description: project.description || "",
                    priority: project.priority ?? 0,
                    start_date: project.start_date?.slice(0, 10) || "",
                    end_date: project.end_date?.slice(0, 10) || "",
                    status: project.status ?? 1,
                  });
                }}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="btn btn-sm d-flex align-items-center gap-1"
                style={{
                  background: "linear-gradient(135deg, #3b82f6, #2563eb)",
                  color: "#fff",
                  borderRadius: 8,
                  fontWeight: 500,
                  fontSize: 13,
                  border: "none",
                }}
                onClick={handleSave}
                disabled={saving}
              >
                <i className="ti ti-check" style={{ fontSize: 15 }} />
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          )}
        </div>

        <div className="card-body bg-white rounded-3">
          {!editing ? (
            /* ─── Read‑only View ─── */
            <div className="d-flex flex-column gap-3">
              {/* Name + Status row */}
              <div className="d-flex align-items-start gap-3">
                <div
                  className="d-flex align-items-center justify-content-center rounded text-white fw-bold"
                  style={{
                    width: 48,
                    height: 48,
                    background: "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)",
                    fontSize: 18,
                    borderRadius: 10,
                    flexShrink: 0,
                  }}
                >
                  {project.key?.slice(0, 2)}
                </div>
                <div className="flex-grow-1 min-width-0">
                  <h5 className="mb-1 fw-semibold" style={{ color: "#0f172a" }}>
                    {project.name}
                  </h5>
                  <div className="d-flex align-items-center gap-2">
                    <span
                      style={{
                        background: "#f1f5f9",
                        padding: "2px 8px",
                        borderRadius: 4,
                        fontSize: 11,
                        color: "#64748b",
                        fontWeight: 600,
                      }}
                    >
                      {project.key}
                    </span>
                    <span
                      className="d-inline-flex align-items-center gap-1"
                      style={{
                        background: sc.bg,
                        color: sc.color,
                        fontSize: 11,
                        fontWeight: 600,
                        padding: "2px 8px",
                        borderRadius: 20,
                      }}
                    >
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          background: sc.dot,
                        }}
                      />
                      {sc.label}
                    </span>
                  </div>
                </div>
              </div>

              {/* Description */}
              {project.description ? (
                <div
                  className="text-body"
                  style={{
                    padding: "12px 14px",
                    background: "#f8fafc",
                    borderRadius: 10,
                    fontSize: 13.5,
                    lineHeight: 1.7,
                  }}
                  dangerouslySetInnerHTML={{ __html: project.description }}
                />
              ) : (
                <div
                  className="text-muted text-center"
                  style={{
                    padding: "20px 14px",
                    background: "#f8fafc",
                    borderRadius: 10,
                    fontSize: 13,
                  }}
                >
                  <i className="ti ti-notes-off d-block mb-2" style={{ fontSize: 24, color: "#cbd5e1" }} />
                  No description provided
                </div>
              )}

              {/* Info Grid */}
              <div className="row g-2">
                <div className="col-6 col-md-3">
                  <InfoBlock
                    icon="ti ti-flag"
                    label="Priority"
                    value={priorityLabel}
                    iconColor="#2563eb"
                    bg="#eff6ff"
                  />
                </div>
                <div className="col-6 col-md-3">
                  <InfoBlock
                    icon="ti ti-calendar"
                    label="Start Date"
                    value={project.start_date ? humanReadableDate(project.start_date) : "—"}
                    iconColor="#475569"
                    bg="#f1f5f9"
                  />
                </div>
                <div className="col-6 col-md-3">
                  <InfoBlock
                    icon="ti ti-calendar-due"
                    label="End Date"
                    value={project.end_date ? humanReadableDate(project.end_date) : "—"}
                    iconColor="#7c3aed"
                    bg="#faf5ff"
                  />
                </div>
                <div className="col-6 col-md-3">
                  <InfoBlock
                    icon="ti ti-user"
                    label="Created By"
                    value={String(creatorName)}
                    iconColor="#059669"
                    bg="#ecfdf5"
                  />
                </div>
              </div>
            </div>
          ) : (
            /* ─── Edit Form ─── */
            <div className="d-flex flex-column gap-3">
              <div>
                <label className="form-label fw-medium" style={{ fontSize: 13, color: "#374151" }}>
                  Project Name
                </label>
                <input
                  type="text"
                  className="form-control"
                  style={{ borderRadius: 8 }}
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                />
              </div>
              <div>
                <label className="form-label fw-medium" style={{ fontSize: 13, color: "#374151" }}>
                  Description
                </label>
                <textarea
                  className="form-control"
                  rows={3}
                  style={{ borderRadius: 8, resize: "vertical" }}
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                />
              </div>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label fw-medium" style={{ fontSize: 13, color: "#374151" }}>
                    Priority
                  </label>
                  <select
                    className="form-select"
                    style={{ borderRadius: 8 }}
                    value={form.priority}
                    onChange={(e) => setForm((p) => ({ ...p, priority: Number(e.target.value) }))}
                  >
                    {PROJECT_PRIORITY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-medium" style={{ fontSize: 13, color: "#374151" }}>
                    Start Date
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    style={{ borderRadius: 8 }}
                    value={form.start_date}
                    onChange={(e) => setForm((p) => ({ ...p, start_date: e.target.value }))}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-medium" style={{ fontSize: 13, color: "#374151" }}>
                    End Date
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    style={{ borderRadius: 8 }}
                    value={form.end_date}
                    onChange={(e) => setForm((p) => ({ ...p, end_date: e.target.value }))}
                  />
                </div>
              </div>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label fw-medium" style={{ fontSize: 13, color: "#374151" }}>
                    Status
                  </label>
                  <select
                    className="form-select"
                    style={{ borderRadius: 8 }}
                    value={form.status}
                    onChange={(e) => setForm((p) => ({ ...p, status: Number(e.target.value) }))}
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ─── Quick Stats Card ─── */}
      <div className="card" style={cardStyle}>
        <div className="card-header border-0 pb-0">
          <h6 className="mb-0 d-flex align-items-center gap-2" style={{ color: "#0f172a" }}>
            <i className="ti ti-chart-pie" style={{ fontSize: 18, color: "#8b5cf6" }} />
            Quick Stats
          </h6>
        </div>
        <div className="card-body bg-white rounded-3">
          <div className="row g-2 mb-3">
            <div className="col-4">
              <StatsBlock label="Total" value={total} color="#0f172a" />
            </div>
            <div className="col-4">
              <StatsBlock label="In Progress" value={inProg} color={STATUS_CATEGORY_COLORS.IN_PROGRESS} />
            </div>
            <div className="col-4">
              <StatsBlock label="Done" value={done} color={STATUS_CATEGORY_COLORS.DONE} />
            </div>
          </div>

          {/* Progress bar */}
          <div className="d-flex align-items-center gap-2 mb-1">
            <span className="text-muted" style={{ fontSize: 12 }}>Completion</span>
            <span className="fw-bold" style={{ fontSize: 13, color: STATUS_CATEGORY_COLORS.DONE }}>
              {completionPct}%
            </span>
          </div>
          <div className="progress" style={{ height: 6, background: "#e5e7eb", borderRadius: 4 }}>
            <div
              className="progress-bar"
              role="progressbar"
              style={{
                width: `${completionPct}%`,
                background: `linear-gradient(90deg, ${STATUS_CATEGORY_COLORS.DONE}, #34d399)`,
                transition: "width 0.5s ease",
                borderRadius: 4,
              }}
            />
          </div>
        </div>
      </div>

      {/* ─── Team Overview Card ─── */}
      <div className="card" style={cardStyle}>
        <div className="card-header border-0 pb-0">
          <h6 className="mb-0 d-flex align-items-center gap-2" style={{ color: "#0f172a" }}>
            <i className="ti ti-users-group" style={{ fontSize: 18, color: "#f59e0b" }} />
            Team ({members.length})
          </h6>
        </div>
        <div className="card-body bg-white rounded-3">
          {members.length === 0 ? (
            <div className="text-center py-3">
              <i className="ti ti-user-off" style={{ fontSize: 32, color: "#cbd5e1" }} />
              <p className="text-muted small mb-0 mt-2">No team members yet.</p>
            </div>
          ) : (
            <div className="d-flex flex-wrap gap-2">
              {members.map((m) => (
                <Tooltip key={m.id} title={`${m.user_snapshot?.name || `User #${m.user_id}`} · ${m.role}`}>
                  <div
                    className="d-flex align-items-center gap-2"
                    style={{
                      padding: "6px 12px",
                      borderRadius: 10,
                      background: "#f1f5f9",
                      transition: "background 0.2s",
                      cursor: "default"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#e5e9ec";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#f1f5f9";
                    }}
                  >
                    <UserAvatar
                      name={m.user_snapshot?.name}
                      img={m.user_snapshot?.img}
                      size={28}
                      fontSize={12}
                    />
                    <div>
                      <div style={{ fontSize: 12.5, fontWeight: 500, color: "#1e293b" }}>
                        {m.user_snapshot?.name || `User #${m.user_id}`}
                      </div>
                    </div>
                    <Tag style={{ margin: 0, fontSize: 10 }}>{m.role}</Tag>
                  </div>
                </Tooltip>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ─── Workflow Columns Card ─── */}
      <div className="card" style={cardStyle}>
        <div className="card-header border-0 pb-0">
          <h6 className="mb-0 d-flex align-items-center gap-2" style={{ color: "#0f172a" }}>
            <i className="ti ti-columns" style={{ fontSize: 18, color: "#10b981" }} />
            Workflow Columns ({statuses.length})
          </h6>
        </div>
        <div className="card-body bg-white rounded-3">
          {statuses.length === 0 ? (
            <div className="text-center py-3">
              <i className="ti ti-layout-columns" style={{ fontSize: 32, color: "#cbd5e1" }} />
              <p className="text-muted small mb-0 mt-2">No workflow columns configured.</p>
            </div>
          ) : (
            <div className="d-flex flex-wrap gap-2">
              {statuses.map((s) => (
                <div
                  key={s.id}
                  className="d-flex align-items-center gap-2"
                  style={{
                    padding: "6px 12px",
                    borderRadius: 8,
                    background: "#f8fafc",
                    fontSize: 13,
                    cursor: "default"
                  }}
                >
                  <span
                    className="d-inline-block rounded-circle"
                    style={{
                      width: 10,
                      height: 10,
                      background: s.color || STATUS_CATEGORY_COLORS[s.category],
                    }}
                  />
                  <span style={{ fontWeight: 500, color: "#1e293b" }}>{s.name}</span>
                  <span
                    className="text-uppercase"
                    style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600 }}
                  >
                    {s.category.replace("_", " ")}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── Helper Components ─── */

interface InfoBlockProps {
  icon: string;
  label: string;
  value: string;
  iconColor: string;
  bg: string;
}

const InfoBlock: React.FC<InfoBlockProps> = ({ icon, label, value, iconColor, bg }) => (
  <div
    style={{
      padding: "10px 12px",
      borderRadius: 10,
      background: bg,
    }}
  >
    <div className="text-muted d-flex align-items-center gap-1" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>
      <i className={icon} style={{ fontSize: 13, color: iconColor }} />
      {label}
    </div>
    <div style={{ fontWeight: 600, fontSize: 14, color: iconColor, marginTop: 2 }}>{value}</div>
  </div>
);

interface StatsBlockProps {
  label: string;
  value: number;
  color: string;
}

const StatsBlock: React.FC<StatsBlockProps> = ({ label, value, color }) => (
  <div
    className="text-center"
    style={{
      padding: "10px 8px",
      borderRadius: 10,
      background: "#f8fafc",
    }}
  >
    <div className="fw-bold" style={{ fontSize: 20, color }}>
      {value}
    </div>
    <div className="text-muted" style={{ fontSize: 11.5 }}>
      {label}
    </div>
  </div>
);

export default DetailsSettings;
